import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Clock,
  Calendar,
  User,
  CheckCircle
} from "lucide-react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { useAuth } from "@/hooks/useAuth";
import { UserRole, ReferralStatus } from "@/lib/schema";
import { subscribeToAdminReferralsWithUI, AdminReferralWithUI } from "@/api/referrals";
import { getReferralSettings } from "@/api/referralSettings";
import { useToast } from "@/hooks/use-toast";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";

export default function PendingProbation() {
  const { userData } = useAuth();
  const { toast } = useToast();
  const [referrals, setReferrals] = useState<AdminReferralWithUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [noticePeriodDays, setNoticePeriodDays] = useState<number>(90); // Default to 90 days
  const [processingReferralId, setProcessingReferralId] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupSubscription = async () => {
      try {
        setLoading(true);

        // Fetch referral settings to get notice period
        const settings = await getReferralSettings();
        if (settings) {
          setNoticePeriodDays(settings.noticePeriodDays);
        }

        // Subscribe to referrals with "hired" status
        unsubscribe = subscribeToAdminReferralsWithUI((allReferrals) => {
          // Filter for referrals with hired status
          const filtered = allReferrals.filter(r => r.status === ReferralStatus.HIRED);
          setReferrals(filtered);
          setLoading(false);
        });
      } catch (error) {
        console.error("Error setting up pending probation subscription:", error);
        setLoading(false);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Only super admins can access this page
  if (userData?.role !== UserRole.SUPER_ADMIN) {
    return (
      <SuperAdminLayout
        title="Pending Probation"
        subtitle="Access Denied"
      >
        <Alert variant="destructive">
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </SuperAdminLayout>
    );
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateProbationEndDate = (hiredDate: Date | undefined) => {
    if (!hiredDate) return null;
    const endDate = new Date(hiredDate);
    endDate.setDate(endDate.getDate() + noticePeriodDays);
    return endDate;
  };

  const getDaysRemaining = (hiredDate: Date | undefined) => {
    if (!hiredDate) return null;
    const endDate = calculateProbationEndDate(hiredDate);
    if (!endDate) return null;
    
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isProbationPeriodComplete = (hiredDate: Date | undefined): boolean => {
    if (!hiredDate) return false;
    const today = new Date();
    const hiredDateObj = new Date(hiredDate);
    const daysSinceHired = Math.floor((today.getTime() - hiredDateObj.getTime()) / (1000 * 60 * 60 * 24));
    return daysSinceHired >= noticePeriodDays;
  };

  const handleMarkProbationPassed = async (referral: AdminReferralWithUI) => {
    setProcessingReferralId(referral.id);
    
    try {
      // Call the Firebase function to mark probation as passed
      const onPassedProbation = httpsCallable(functions, 'onPassedProbation');
      const result = await onPassedProbation({ referralId: referral.id });
      
      const data = result.data as { 
        success?: boolean; 
        data?: { message?: string; referralId?: string };
        error?: { message?: string; code?: string };
      };
      
      if (data.success && data.data) {
        toast({
          title: "Probation Passed",
          description: data.data.message || "Candidate successfully marked as passed probation and reward is now eligible.",
        });
      } else if (data.error) {
        throw new Error(data.error.message || "Failed to mark candidate as passed probation");
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (error: any) {
      console.error("Error marking probation as passed:", error);
      
      toast({
        title: "Error",
        description: error.message || "Failed to mark candidate as passed probation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingReferralId(null);
    }
  };

  if (loading) {
    return (
      <SuperAdminLayout
        title="Pending Probation"
        subtitle="Track candidates currently in their probation period"
      >
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (referrals.length === 0) {
    return (
      <SuperAdminLayout
        title="Pending Probation"
        subtitle="Track candidates currently in their probation period"
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-2">
            <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No candidates in probation period</p>
            <p className="text-gray-400 text-sm">All hired candidates have completed their probation</p>
          </div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout
      title="Pending Probation"
      subtitle="Track candidates currently in their probation period"
    >
      <Card>
        <CardHeader>
          <CardTitle className="space-grotesk">Hired Candidates in Probation Period</CardTitle>
          <CardDescription>
            Monitor probation periods for recently hired candidates
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    Candidate
                  </th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    Job
                  </th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    Hired Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    <span className="hidden lg:inline">Probation End</span>
                    <span className="lg:hidden">End Date</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    <span className="hidden sm:inline">Days Remaining</span>
                    <span className="sm:hidden">Days</span>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {referrals.map((referral) => {
                  const daysRemaining = getDaysRemaining(referral.hiredAt);
                  const probationEndDate = calculateProbationEndDate(referral.hiredAt);
                  const canMarkComplete = isProbationPeriodComplete(referral.hiredAt);
                  
                  return (
                    <tr key={referral.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-slate-900 space-grotesk truncate">
                              {referral.candidateName}
                            </span>
                          </div>
                          <div className="md:hidden text-xs text-slate-500 space-grotesk ml-6">
                            {referral.jobTitle}
                          </div>
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-slate-900 space-grotesk">
                          {referral.jobTitle}
                        </div>
                      </td>
                      <td className="hidden xl:table-cell px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-600 space-grotesk">
                            {formatDate(referral.hiredAt)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-600 space-grotesk">
                          {probationEndDate ? formatDate(probationEndDate) : "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {daysRemaining !== null ? (
                          <Badge 
                            variant={daysRemaining <= 7 ? "destructive" : daysRemaining <= 30 ? "default" : "secondary"}
                            className="space-grotesk text-xs"
                          >
                            {daysRemaining > 0 ? `${daysRemaining}d` : "Due"}
                          </Badge>
                        ) : (
                          <span className="text-sm text-slate-400 space-grotesk">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleMarkProbationPassed(referral)}
                          disabled={!canMarkComplete || processingReferralId === referral.id}
                          className="space-grotesk"
                          title={canMarkComplete ? "Mark probation as passed" : `Available after ${noticePeriodDays} days`}
                        >
                          {processingReferralId === referral.id ? (
                            <>
                              <div className="h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin lg:mr-2" />
                              <span className="hidden lg:inline">Processing...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 lg:mr-2" />
                              <span className="hidden lg:inline">Mark Complete</span>
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </SuperAdminLayout>
  );
}

